import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { LifeBuoy, User, Shield, Mail, Plug } from "lucide-react";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { PageLayout } from "@/components/layout/PageLayout";
import { NavigationSidebar } from "@/components/navigation";
import type { NavItem } from "@/components/navigation";
import { useSidebarState } from "@/components/navigation/hooks/useSidebarState";
import { SidebarBottomMenus } from "@/components/navigation/organisms/SidebarBottomMenus";
import { FranklinLogo } from "@/components/ui/franklin-logo";
import { PageTitle } from "@/components/ui/page-title";
import { cn } from "@/lib/utils";
import { PageSubtitle } from "@/components/ui/page-subtitle";
import type { PageInfoId } from "@/lib/i18n/types/pageInfo";
import { PersonalSettingsForm } from "@/components/settings/PersonalSettingsForm";
import { SecuritySettingsForm } from "@/components/settings/SecuritySettingsForm";
import { UserInvitationsList } from "@/components/settings/UserInvitationsList";
import { ConnectedApps } from "@/components/settings/ConnectedApps";
import { PlatformGuide } from "@/components/page-info/PlatformGuide";
import { PAGE_INFO_GUIDE_ROUTE } from "@/components/page-info/pageInfoRegistry";

type SettingsSection = "personal" | "security" | "invitations" | "connections" | "help";

const SETTINGS_PAGE_INFO: Record<SettingsSection, PageInfoId> = {
  personal: "settingsPersonal",
  security: "settingsSecurity",
  invitations: "settingsInvitations",
  connections: "settingsConnections",
  help: "help",
};

const SIDEBAR_SCOPE = "user-settings";

export default function Settings() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const section = searchParams.get("section");
  // Help has a path of its own (/settings/help) — "More in the Help centre"
  // and the old /help link there; the other sections stay on ?section=.
  const activeSection: SettingsSection =
    pathname === PAGE_INFO_GUIDE_ROUTE
      ? "help"
      : section === "security" || section === "invitations" || section === "connections"
        ? section
        : "personal";
  const setActiveSection = (value: SettingsSection) =>
    navigate(value === "help" ? PAGE_INFO_GUIDE_ROUTE : `/settings?section=${value}`);

  const { isCollapsed: isNavCollapsed, toggleCollapse: toggleNavCollapse } = useSidebarState({
    buildingId: SIDEBAR_SCOPE,
    defaultCollapsed: false,
  });

  // Each section owns the page heading — "Security" and "Invitations" are the
  // page titles on their own pages, not sub-headings under a "User settings" one.
  const pageHeader: Record<SettingsSection, { title: string; subtitle: string }> = {
    personal: { title: t.settings.title, subtitle: t.settings.personal.description },
    security: {
      title: t.settings.security,
      subtitle: t.settings.securitySection.changePasswordDesc,
    },
    invitations: {
      title: t.common.invitations,
      subtitle: t.settings.invitations.pageSubtitle,
    },
    connections: {
      title: t.connectors.connectedTitle,
      subtitle: t.connectors.connectedDescription,
    },
    help: { title: t.pageInfo.guide.title, subtitle: t.pageInfo.guide.subtitle },
  };

  const navItems: NavItem[] = [
    {
      id: "personal",
      label: t.common.personalSettings,
      icon: User,
      isActive: activeSection === "personal",
      onClick: () => setActiveSection("personal"),
    },
    {
      id: "security",
      label: t.settings.security,
      icon: Shield,
      isActive: activeSection === "security",
      onClick: () => setActiveSection("security"),
    },
    {
      id: "connections",
      label: t.connectors.connectedTitle,
      icon: Plug,
      isActive: activeSection === "connections",
      onClick: () => setActiveSection("connections"),
    },
    {
      id: "invitations",
      label: t.common.invitations,
      icon: Mail,
      isActive: activeSection === "invitations",
      onClick: () => setActiveSection("invitations"),
    },
    {
      id: "help",
      label: t.pageInfo.guide.title,
      icon: LifeBuoy,
      isActive: activeSection === "help",
      onClick: () => setActiveSection("help"),
    },
  ];

  return (
    <PageLayout showHeader={false} customPadding>
      <div className="flex h-screen w-full">
        <NavigationSidebar
          buildingId={SIDEBAR_SCOPE}
          items={navItems}
          logo={
            <FranklinLogo
              size="sm"
              expanded={!isNavCollapsed}
              showBorder={false}
              onClick={() => navigate("/buildings")}
              className="bg-transparent"
            />
          }
          footer={({ isCollapsed }) => <SidebarBottomMenus isCollapsed={isCollapsed} />}
          isCollapsed={isNavCollapsed}
          onToggleCollapse={toggleNavCollapse}
        />
        {/*
          `relative` makes this scroller the containing block for absolutely
          positioned descendants. Radix Checkbox/Switch render a hidden
          `position: absolute` native input when used inside a <form>; without a
          positioned ancestor those inputs escape the scroller (and PageLayout's
          overflow-hidden) into the initial containing block, stretching the
          document itself and adding dead white space below the app.
        */}
        <div className="relative flex-1 overflow-auto p-6">
          {/* Centered content column — matches the Organization Settings page.
              Help is a long reading page, so it starts at the left edge instead. */}
          <div className={cn("max-w-4xl space-y-8", activeSection !== "help" && "mx-auto")}>
            <div className="space-y-1">
              <PageTitle info={SETTINGS_PAGE_INFO[activeSection]}>
                {pageHeader[activeSection].title}
              </PageTitle>
              <PageSubtitle>{pageHeader[activeSection].subtitle}</PageSubtitle>
            </div>

            {activeSection === "personal" && <PersonalSettingsForm />}
            {activeSection === "security" && <SecuritySettingsForm />}
            {activeSection === "invitations" && <UserInvitationsList />}
            {activeSection === "connections" && <ConnectedApps />}
            {activeSection === "help" && <PlatformGuide />}
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
